import React, {Component} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import RecommenderService from "../../../../services/recommender-service/RecommenderService";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const canRecord = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);

// Each segment is a complete standalone file (we restart the recorder rather than use
// MediaRecorder's timeslice, whose later chunks lack the container header and cannot be
// transcribed on their own). 6s keeps the feedback near-live — text lands a beat behind
// the speaker; whisper-turbo handles short clips well, and the sequence buffer keeps
// out-of-order responses from scrambling sentences. Below ~5s the mid-word boundary
// cuts start to hurt accuracy more than the latency is worth.
const SEGMENT_MS = 6000;
// A segment this small is a silent gap or the tail of an instant stop — not worth a call.
const MIN_BLOB_BYTES = 1000;

// Chrome hands back a fresh error code for each failure mode; only the ones a clinician
// can act on are worth words, the rest read as "it stopped".
const ERROR_MESSAGE = {
    "not-allowed": "Microphone blocked — allow it in the address bar and try again.",
    "service-not-allowed": "Microphone blocked by the browser or OS.",
    "no-speech": "Nothing heard — mic still on.",
    "audio-capture": "No microphone found.",
    "network": "Speech service unreachable.",
};

// getUserMedia rejects with a DOMException whose name is the whole diagnosis. Reporting
// them all as "blocked" sends the clinician to a permission prompt that is already
// granted, while the real cause — a headset another app is holding, or an OS-level
// block — goes unnamed.
const CAPTURE_ERROR_MESSAGE = {
    NotAllowedError: "Microphone blocked — allow it in the address bar and try again.",
    SecurityError: "Microphone blocked — allow it in the address bar and try again.",
    NotFoundError: "No microphone found — plug one in or pick another device.",
    OverconstrainedError: "No microphone found — plug one in or pick another device.",
    NotReadableError: "Microphone is in use by another app — close it and try again.",
    AbortError: "The microphone could not be opened — try again.",
};

const captureErrorMessage = (error) => {
    const name = error && error.name;
    if (CAPTURE_ERROR_MESSAGE[name]) return CAPTURE_ERROR_MESSAGE[name];
    return "Microphone unavailable (" + (name || "unknown error") + ").";
};

/**
 * Push-to-dictate button. Two engines behind the same mic icon:
 *
 *  - whisper (default when the browser can record): MediaRecorder captures ~15s
 *    segments, each is uploaded to the recommender's /transcribe (Whisper) and the
 *    returned text is appended. Segments can come back out of order, so results are
 *    parked in a sequence buffer and appended strictly in recording order — otherwise
 *    a slow chunk would splice its sentence into the middle of a later one.
 *  - webspeech: the browser's own recognition, kept as the fallback when recording is
 *    unavailable or the server answers 503 (transcription not configured).
 *
 * Finalised text is appended to whatever the textarea currently holds via onAppend,
 * rather than the component rewriting the whole value from a snapshot it took when
 * dictation started. That is what lets the clinician keep typing (or correcting an
 * earlier sentence) while still talking.
 */
export default class Dictation extends Component {

    state = {
        listening: false,
        interim: "",
        pending: 0, // whisper segments uploaded and not yet answered
        error: null,
        mode: canRecord ? "whisper" : (SpeechRecognition ? "webspeech" : null),
    };

    componentWillUnmount() {
        this.stop();
    }

    // --- whisper engine -------------------------------------------------------

    startWhisper = () => {
        navigator.mediaDevices.getUserMedia({audio: true}).then(stream => {
            this.stream = stream;
            // Reset the ordering buffer per dictation session.
            this.seq = 0;
            this.nextToAppend = 0;
            this.results = {};
            this.live = true;
            this.setState({listening: true, pending: 0, error: null});
            this.startSegment();
        }).catch(error => {
            // The name is the only actionable detail, and it is gone once this promise
            // settles — log it so a staging report can be diagnosed without a repro.
            console.warn("[Dictation] getUserMedia failed:", error && error.name, error && error.message);
            this.setState({error: captureErrorMessage(error)});
        });
    };

    startSegment = () => {
        if (!this.live || !this.stream) return;
        let recorder;
        try {
            const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined;
            recorder = new MediaRecorder(this.stream, mimeType ? {mimeType} : undefined);
        } catch (e) {
            this.setState({error: "Recording failed — using browser dictation instead."});
            this.fallBackToWebSpeech();
            return;
        }
        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size >= MIN_BLOB_BYTES) this.sendSegment(event.data);
        };
        recorder.onstop = () => {
            if (this.live && this.recorder === recorder) this.startSegment();
        };
        this.recorder = recorder;
        recorder.start();
        this.segmentTimer = setTimeout(() => {
            if (this.recorder === recorder && recorder.state === "recording") recorder.stop();
        }, SEGMENT_MS);
    };

    sendSegment = (blob) => {
        const id = this.seq++;
        this.setState(s => ({pending: s.pending + 1}));
        RecommenderService.transcribe(blob,
            data => this.landSegment(id, data && data.text ? data.text : ""),
            err => {
                // 503 = the server has no speech key configured; whisper will never
                // work this session, so switch engines instead of erroring per chunk.
                if (err && err.response && err.response.status === 503) {
                    this.landSegment(id, "");
                    this.setState({error: "Server dictation unavailable — using browser dictation."});
                    this.fallBackToWebSpeech();
                } else {
                    this.landSegment(id, "");
                    this.setState({error: "A segment failed to transcribe — check the note for gaps."});
                }
            });
    };

    // Append strictly in recording order: park each result until every earlier
    // segment has landed, then flush the run.
    landSegment = (id, text) => {
        this.results[id] = text;
        while (this.results[this.nextToAppend] !== undefined) {
            const t = this.results[this.nextToAppend];
            delete this.results[this.nextToAppend];
            this.nextToAppend++;
            if (t && t.trim()) this.props.onAppend(t.trim());
        }
        this.setState(s => ({pending: Math.max(0, s.pending - 1)}));
    };

    stopWhisper = () => {
        this.live = false;
        clearTimeout(this.segmentTimer);
        const recorder = this.recorder;
        this.recorder = null;
        if (recorder && recorder.state === "recording") {
            try {
                recorder.stop(); // final ondataavailable still fires and uploads the tail
            } catch (e) {
                // already stopped
            }
        }
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        this.setState({listening: false});
    };

    fallBackToWebSpeech = () => {
        this.stopWhisper();
        if (!SpeechRecognition) {
            this.setState({mode: null});
            return;
        }
        this.setState({mode: "webspeech"}, this.startWebSpeech);
    };

    // --- webspeech engine (fallback) -------------------------------------------

    startWebSpeech = () => {
        if (!SpeechRecognition || this.recognition) return;

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = this.props.lang || "en-US";

        recognition.onresult = (event) => {
            let interim = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    this.props.onAppend(transcript.trim());
                } else {
                    interim += transcript;
                }
            }
            this.setState({interim});
        };

        recognition.onerror = (event) => {
            const message = ERROR_MESSAGE[event.error] || "Dictation stopped.";
            // "no-speech" fires during a normal pause and the engine keeps going;
            // treating it as fatal would stop dictation every time the clinician thinks.
            if (event.error === "no-speech") {
                this.setState({error: message});
                return;
            }
            this.setState({error: message});
            this.stop();
        };

        // Chrome ends the session on its own after a silence even in continuous mode.
        // While the clinician still has the mic on, that is a restart, not a stop.
        recognition.onend = () => {
            if (this.state.listening && this.recognition === recognition) {
                try {
                    recognition.start();
                } catch (e) {
                    this.stop();
                }
            }
        };

        this.recognition = recognition;
        this.setState({listening: true, interim: "", error: null});
        try {
            recognition.start();
        } catch (e) {
            this.setState({listening: false});
            this.recognition = null;
        }
    };

    stopWebSpeech = () => {
        const recognition = this.recognition;
        this.recognition = null;
        this.setState({listening: false, interim: ""});
        if (recognition) {
            recognition.onend = null;
            recognition.onresult = null;
            recognition.onerror = null;
            try {
                recognition.stop();
            } catch (e) {
                // already stopped
            }
        }
    };

    // --- shared ----------------------------------------------------------------

    start = () => this.state.mode === "whisper" ? this.startWhisper() : this.startWebSpeech();

    stop = () => {
        this.stopWhisper();
        this.stopWebSpeech();
    };

    toggle = () => this.state.listening ? this.stop() : this.start();

    // The control lives inside the textarea, so it is an icon and nothing else: a labelled
    // button there would sit on top of the note the clinician is writing. What is being
    // heard still needs saying out loud, so the status runs along the same strip to the
    // left of the button rather than pushing the textarea around.
    render() {
        const {listening, interim, pending, error, mode} = this.state;

        if (!mode) {
            return (
                <div className="emr-check-dictation">
                    <span className="emr-check-reason"
                          title="Dictation needs microphone recording or Chrome/Edge speech recognition. Type the note instead.">
                        Dictation unavailable
                    </span>
                </div>
            );
        }

        const title = listening ? "Stop dictation" : "Dictate the note";
        const busy = pending > 0;

        return (
            <div className="emr-check-dictation">
                <span className="emr-check-interim">
                    {listening && !interim && !busy && !error && <span className="emr-check-mic-live">listening…</span>}
                    {busy && <span className="emr-check-mic-live">transcribing…</span>}
                    {interim && <span>{interim}</span>}
                    {error && <span className="emr-check-mic-error">{error}</span>}
                </span>
                <button type="button" title={title} aria-label={title} aria-pressed={listening}
                        className={"emr-check-mic" + (listening ? " on" : "")}
                        onClick={this.toggle}>
                    <FontAwesomeIcon icon={["fas", listening ? "stop" : "microphone"]}/>
                </button>
            </div>
        );
    }
}
